import React from "react";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Badge,
    Box,
    Button,
    Circle,
    Flex,
    Heading,
    HStack,
    Icon,
    Link,
    SimpleGrid,
    Stack,
    Text,
    Wrap,
    WrapItem,
    keyframes,
    useColorModeValue,
} from "@chakra-ui/react";
import {
    MdAccessTime,
    MdCampaign,
    MdDevices,
    MdLibraryMusic,
    MdNotificationsActive,
    MdOpenInNew,
    MdPlaylistAddCheck,
    MdSchedule,
    MdSchool,
    MdViewList,
} from "react-icons/md";

const WEBSITE_URL = "https://digitalghanti.com";

const features = [
    {
        icon: MdSchedule,
        title: "Automatic Bell Scheduling",
        text: "Plan full-day bell routines with period-wise timing, break schedules, assemblies and closing bells.",
        color: "teal",
    },
    {
        icon: MdViewList,
        title: "Flexible Roster Management",
        text: "Create different schedules for weekdays, exam days, events, holidays and special institution routines.",
        color: "blue",
    },
    {
        icon: MdLibraryMusic,
        title: "Music & Audio Library",
        text: "Upload and manage music, voice messages, prayers, national anthem, warning tones and custom alerts.",
        color: "purple",
    },
    {
        icon: MdNotificationsActive,
        title: "Emergency Alerts",
        text: "Trigger instant fire, emergency and warning tones across the school speakers with a single tap.",
        color: "red",
    },
    {
        icon: MdCampaign,
        title: "Virtual Announcements",
        text: "Speak announcements with text-to-voice, play pre-recorded audio, and schedule them — even offline.",
        color: "orange",
    },
    {
        icon: MdDevices,
        title: "Runs On Any Device",
        text: "Works from any computer, tablet or phone browser — no extra hardware or installation required.",
        color: "cyan",
    },
];

const highlights = [
    {icon: MdNotificationsActive, label: "Automated Belling Software"},
    {icon: MdDevices, label: "Runs on any device"},
    {icon: MdAccessTime, label: "Proper time management"},
    {icon: MdSchool, label: "Modernising schools & colleges"},
];

const mechanism = [
    {
        step: "1",
        title: "Set up your roster",
        text: "Add a roster for each routine — regular days, exam days or events — so the system knows which schedule to follow.",
    },
    {
        step: "2",
        title: "Add bells & sounds",
        text: "Create bells with their exact time and choose a sound from the audio library for each period or break.",
    },
    {
        step: "3",
        title: "The scheduler runs automatically",
        text: "Digital Ghanti checks the time every second in the background and rings the right bell at the right moment.",
    },
    {
        step: "4",
        title: "Control & monitor live",
        text: "Use the dashboard, remote control, device status and activity logs to keep everything running on time.",
    },
];

const howToUse = [
    {
        q: "How do I create a bell schedule?",
        a: "Open Roster, choose or create a roster, then use “Add Bell” to set the bell name, time and sound. Saved bells start ringing automatically at their scheduled time.",
    },
    {
        q: "How do I change a bell time?",
        a: "In the bell list, click Edit on a bell, pick the new time in the “new bell time” field and press Save. The change applies immediately.",
    },
    {
        q: "How do I play an emergency alert?",
        a: "From the Bell Dashboard remote, trigger the fire/emergency tone to instantly play the warning sound on all connected speakers.",
    },
    {
        q: "How do I send an announcement?",
        a: "Open Virtual Announcement, type your message for text-to-voice or record audio, preview it, then play now or schedule it for later.",
    },
    {
        q: "How do I check connected speakers?",
        a: "Open Bell Dashboard → Devices tab to see all speakers and line-in inputs with live connected / disconnected status.",
    },
];

const float = keyframes`
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-6px); }
`;

function Help() {
    const cardBg = useColorModeValue("white", "gray.700");
    const pageBg = useColorModeValue("gray.50", "gray.800");
    const subText = useColorModeValue("gray.600", "gray.300");

    return (
        <Flex
            direction="column"
            pt={{base: "110px", md: "80px"}}
            px={{base: "4", md: "8"}}
            pb="10"
            bg={pageBg}
            minH="100vh"
        >
            {/* Hero */}
            <Box
                borderRadius="20px"
                p={{base: "6", md: "12"}}
                color="white"
                bgGradient="linear(135deg, teal.400, blue.500 55%, purple.500)"
                boxShadow="xl"
                position="relative"
                overflow="hidden"
            >
                <Icon
                    as={MdNotificationsActive}
                    boxSize={{base: "120px", md: "180px"}}
                    position="absolute"
                    right={{base: "-20px", md: "20px"}}
                    top="50%"
                    transform="translateY(-50%)"
                    opacity={0.15}
                    animation={`${float} 4s ease-in-out infinite`}
                />
                <Badge colorScheme="whiteAlpha" bg="whiteAlpha.300" mb={4} px={3} py={1} borderRadius="full">
                    Digital Ghanti · Help & Guide
                </Badge>
                <Heading size={{base: "lg", md: "xl"}} maxW="760px" lineHeight="1.2">
                    Everything needed for a modern automated bell system
                </Heading>
                <Text mt={4} fontSize={{base: "md", md: "lg"}} maxW="720px" opacity={0.95}>
                    Digital Ghanti combines flexible scheduling, audio control, emergency alerts and reporting in one
                    easy-to-use platform.
                </Text>
                <Button
                    as={Link}
                    href={WEBSITE_URL}
                    isExternal
                    mt={6}
                    size="lg"
                    bg="white"
                    color="blue.600"
                    rightIcon={<MdOpenInNew/>}
                    _hover={{bg: "whiteAlpha.900", transform: "translateY(-2px)", textDecoration: "none"}}
                    transition="all 0.2s ease"
                >
                    Visit digitalghanti.com
                </Button>
            </Box>

            {/* Highlight chips */}
            <Wrap spacing={3} mt={6} justify={{base: "flex-start", md: "center"}}>
                {highlights.map((item) => (
                    <WrapItem key={item.label}>
                        <HStack
                            bg={cardBg}
                            px={4}
                            py={2}
                            borderRadius="full"
                            boxShadow="sm"
                            border="1px solid"
                            borderColor="gray.100"
                            transition="all 0.2s ease"
                            _hover={{transform: "translateY(-2px)", boxShadow: "md"}}
                        >
                            <Icon as={item.icon} color="teal.500"/>
                            <Text fontSize="sm" fontWeight="medium">{item.label}</Text>
                        </HStack>
                    </WrapItem>
                ))}
            </Wrap>

            {/* Features */}
            <Box mt={10}>
                <Heading size="md" mb={1}>Powerful features</Heading>
                <Text color={subText} mb={6}>Built specifically for schools, colleges and institutions.</Text>
                <SimpleGrid columns={{base: 1, md: 2, lg: 3}} spacing={5}>
                    {features.map((feature) => (
                        <Box
                            key={feature.title}
                            bg={cardBg}
                            p={6}
                            borderRadius="16px"
                            boxShadow="sm"
                            border="1px solid"
                            borderColor="gray.100"
                            transition="all 0.22s ease"
                            cursor="default"
                            _hover={{
                                transform: "translateY(-6px)",
                                boxShadow: "xl",
                                borderColor: `${feature.color}.200`,
                            }}
                            role="group"
                        >
                            <Circle
                                size="52px"
                                bg={`${feature.color}.50`}
                                color={`${feature.color}.500`}
                                mb={4}
                                transition="all 0.22s ease"
                                _groupHover={{bg: `${feature.color}.500`, color: "white"}}
                            >
                                <Icon as={feature.icon} boxSize="26px"/>
                            </Circle>
                            <Heading size="sm" mb={2}>{feature.title}</Heading>
                            <Text color={subText} fontSize="sm">{feature.text}</Text>
                        </Box>
                    ))}
                </SimpleGrid>
            </Box>

            {/* How it works / mechanism */}
            <Box mt={12}>
                <Heading size="md" mb={1}>How the system works</Heading>
                <Text color={subText} mb={6}>From setup to automatic ringing in four simple steps.</Text>
                <SimpleGrid columns={{base: 1, md: 2, lg: 4}} spacing={5}>
                    {mechanism.map((item) => (
                        <Box
                            key={item.step}
                            bg={cardBg}
                            p={6}
                            borderRadius="16px"
                            boxShadow="sm"
                            border="1px solid"
                            borderColor="gray.100"
                            position="relative"
                            transition="all 0.2s ease"
                            _hover={{transform: "translateY(-4px)", boxShadow: "lg"}}
                        >
                            <Circle size="40px" bg="teal.500" color="white" fontWeight="bold" mb={4}>
                                {item.step}
                            </Circle>
                            <Heading size="sm" mb={2}>{item.title}</Heading>
                            <Text color={subText} fontSize="sm">{item.text}</Text>
                        </Box>
                    ))}
                </SimpleGrid>
            </Box>

            {/* How to use / FAQ */}
            <Box mt={12}>
                <Heading size="md" mb={1}>How to use the features</Heading>
                <Text color={subText} mb={6}>Quick answers to get you started.</Text>
                <Accordion allowToggle bg={cardBg} borderRadius="16px" boxShadow="sm" overflow="hidden">
                    {howToUse.map((item) => (
                        <AccordionItem key={item.q} border="none" borderBottom="1px solid" borderColor="gray.100">
                            <AccordionButton py={4} _hover={{bg: "teal.50"}}>
                                <HStack flex="1" textAlign="left" spacing={3}>
                                    <Icon as={MdPlaylistAddCheck} color="teal.500"/>
                                    <Text fontWeight="semibold">{item.q}</Text>
                                </HStack>
                                <AccordionIcon/>
                            </AccordionButton>
                            <AccordionPanel pb={4} color={subText} pl={12}>
                                {item.a}
                            </AccordionPanel>
                        </AccordionItem>
                    ))}
                </Accordion>
            </Box>

            {/* Bottom CTA */}
            <Flex
                mt={12}
                direction={{base: "column", md: "row"}}
                align="center"
                justify="space-between"
                gap={4}
                bg={cardBg}
                p={{base: 6, md: 8}}
                borderRadius="16px"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.100"
            >
                <Stack spacing={1}>
                    <Heading size="md">Want to learn more about Digital Ghanti?</Heading>
                    <Text color={subText}>
                        Explore full details, updates and support on our website.
                    </Text>
                </Stack>
                <Button
                    as={Link}
                    href={WEBSITE_URL}
                    isExternal
                    colorScheme="teal"
                    size="lg"
                    rightIcon={<MdOpenInNew/>}
                    _hover={{textDecoration: "none", transform: "translateY(-2px)"}}
                    transition="all 0.2s ease"
                >
                    Go to digitalghanti.com
                </Button>
            </Flex>
        </Flex>
    );
}

export default Help;
